import {Helmet} from '@dr.pogodin/react-helmet';
import {
  Table,
  TableRow,
  TableRowActionsData,
  TableRowAction,
  openDeleteDialog,
} from 'platform/components';
import {VStack, Text, Align} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {selectBranchList} from '@dms/api/core';
import {
  useGetDocumentLimitDefinitionListQuery,
  useRemoveDocumentLimitDefinitionMutation,
} from '@dms/api/finance';
import {BranchListItemResponseBody, DocumentLimitDefinitionResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

export const CashLimits = () => {
  const {data: list, isLoading, isError} = useGetDocumentLimitDefinitionListQuery({});
  const navigate = useNavigate();
  const [removeDefinition] = useRemoveDocumentLimitDefinitionMutation();

  const {data} = useSelector(selectBranchList);
  const branches = data?.branchListItems;

  const handleItemDeletion = (id: string) => {
    removeDefinition({id}).unwrap().catch(handleApiError);
  };

  const handleRowClick = (row: DocumentLimitDefinitionResponseBody) => {
    navigate(composePath(settingsRoutes.cashLimitsEdit, {params: {id: row.id}}));
  };

  const columns = [
    {element: <Align left>{i18n.t('entity.branch.labels.branch')}</Align>},
    {element: <Align left>{i18n.t('general.labels.currency')}</Align>},
  ];

  const renderRow = (row: DocumentLimitDefinitionResponseBody, index: number) => {
    const getContent = () => {
      if (row.branchId == null) {
        return i18n.t('entity.tenant.labels.tenant');
      }
      const found = (branches as BranchListItemResponseBody[]).find(
        (branch) => branch.id === row.branchId
      );
      return found?.marketingName ?? '-';
    };

    const actions: TableRowActionsData = {
      primary: [
        {
          title: i18n.t('general.labels.edit'),
          icon: 'content/create' as const,
          onClick: () =>
            navigate(composePath(settingsRoutes.cashLimitsEdit, {params: {id: row.id}})),
        },
        ...(row.system
          ? []
          : [
              {
                title: i18n.t('general.labels.delete'),
                icon: 'action/delete' as const,
                onClick: () =>
                  openDeleteDialog({
                    text: i18n.t(
                      'entity.cashRegisterLimits.notifications.deleteLimitDefinitionNotification'
                    ),
                    onConfirm: () => handleItemDeletion(row.id),
                  }),
              } as TableRowAction,
            ]),
      ],
    };

    return (
      <TableRow
        key={row.id}
        onRowClick={() => handleRowClick(row)}
        actions={actions}
        data-testid={testIds.settings.cashLimits(`row-${index}`)}
      >
        <strong>
          {`${getContent()} ${row.system ? `(${i18n.t('general.labels.system')})` : ''}`}
        </strong>
        <Text>{row.currency}</Text>
      </TableRow>
    );
  };

  const title = i18n.t('page.accounting.labels.cashRegisterLimitsTitle');

  const header: SettingsTemplateHeader = {
    title,
    actions: [
      {
        type: 'button',
        title: i18n.t('page.accounting.actions.addDefinition'),
        onClick: () => {
          navigate(settingsRoutes.cashLimitsAdd);
        },
        'data-testid': testIds.settings.cashLimits('createNew'),
      },
    ],
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={header}
        data-testid="settings-cash-limits"
        isLoading={isLoading}
        isError={isError}
      >
        <VStack spacing={4}>
          <Table columns={columns}>{(list ?? []).map((row, index) => renderRow(row, index))}</Table>
        </VStack>
      </SettingsTemplate>
    </>
  );
};
